import React, { useEffect, useState } from 'react';
import { TableFooterContainer, TableFooterPagination, TableFooterSize, TableHeadContainer, TableHeadFilter, TableHeadTitle } from './styles';
import { ActionsView, TableView } from '../Controls/table';
import { sleep } from '../../services/app.service';
import { Button, Form, Select, AutoComplete } from 'antd';
import { message } from '../../contexts/App';
import { SelectFormServiceView, TabEditView } from '../Form/index.';
import produce from 'immer';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import { CATEGORY } from '../../pages/trail/list';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { normalizeVietnamese } from '../../utils';

const TableContext = React.createContext<any>({});

export const TableProvider = ({ children, removeService, listService, paging, initQuery, dataType }: any) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [query, setQuery] = useState<any>({});

    useEffect(() => {
        loadData().then();
    }, [page, limit, query, initQuery]);

    const loadData = async () => {
        setLoading(true);
        let category = query?.category ? (Array.isArray(query.category) ? query.category.join(',') : query.category) : initQuery?.category;
        if (query) {
            Object.keys(query).forEach((key) => {
                if (Array.isArray(query[key])) {
                    query[key] = query[key].join(',');
                }
            });
        }

        const result = await listService(
            paging
                ? {
                      page,
                      limit,
                      ...query,
                      ...(initQuery || {}),
                      category,
                  }
                : {
                      ...query,
                      ...(initQuery || {}),
                      category,
                  }
        );
        if (result.status === 200 && result?.data?.data?.items?.length >= 0) {
            setRows(result?.data?.data?.items);
            setTotal(result.data?.data?.page_info?.total || result?.data?.data?.length || 0);
        } else {
            if (result?.data?.data?.length >= 0) {
                setRows(result?.data?.data);
                setTotal(result?.data?.data?.length || 0);
            } else {
                setRows([]);
                setTotal(0);
            }
        }
        setLoading(false);
    };

    const onExport = async () => {
        setLoading(true);
        let category = query?.category ? query?.category : initQuery?.category;
        let limit = 1000;
        let page = 1;
        const result = await listService(
            paging
                ? {
                      page: page,
                      limit: limit,
                      ...query,
                      ...(initQuery || {}),
                      export_csv: true,
                      category,
                  }
                : {
                      page: page,
                      limit: limit,
                      export_csv: true,
                      ...query,
                      ...(initQuery || {}),
                      category,
                  }
        );
        let totalPage = Math.ceil(result?.data?.data?.page_info?.total / limit);
        if (result.status === 200 && result?.data?.data?.items?.length >= 0) {
            let itemDraft: any[] = result?.data?.data?.items ?? [];
            let getItem = async (i: number) => {
                let result2 = await listService(
                    paging
                        ? {
                              page: i,
                              limit: limit,
                              ...query,
                              ...(initQuery || {}),
                              export_csv: true,
                              category,
                          }
                        : {
                              page: i,
                              limit: limit,
                              export_csv: true,
                              ...query,
                              ...(initQuery || {}),
                              category,
                          }
                );
                itemDraft = [...itemDraft, ...result2?.data?.data?.items];
            };
            let promiseList = [];
            for (let i = 2; i <= totalPage; i++) {
                promiseList.push(getItem(i));
            }
            Promise.all(promiseList).then(async (results) => {
                const isUserData = dataType === 'user';

                let itemList = itemDraft.map((i: any, index: number) => {
                    // Check if this is user data
                    if (isUserData) {
                        return {
                            id: i.id || '',
                            username: i.first_name + ' ' + i.last_name || '',
                            first_name: i.first_name || '',
                            last_name: i.last_name || '',
                            email: i.email || '',
                            created_at: i.createdAt ? moment(i.createdAt).format('hh:mm A DD/MM/YYYY') : '',
                        };
                    }

                    // Check if this is ranking data (has user object and activity stats)
                    if (i.user && (i.activity_count !== undefined || i.total_distance !== undefined)) {
                        // Handle ranking data export
                        const formatDistance = (distance: number) => {
                            return distance >= 1000 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance)} m`;
                        };

                        return {
                            rank: index + 1,
                            user_name: i.user?.first_name || i.user?.name || 'Unknown User',
                            email: i.user?.email || 'No email',
                            activity_count: i.activity_count || 0,
                            total_distance: i.total_distance ? `${i.total_distance.toFixed(2)} km` : '0 km',
                            total_elevation_gain: i.total_elevation_gain ? formatDistance(i.total_elevation_gain) : '0 m',
                            total_elevation_loss: i.total_elevation_loss ? formatDistance(i.total_elevation_loss) : '0 m',
                            total_moving_time: i.total_moving_time ? formatDistance(i.total_moving_time) : '0 m',
                            last_activity: i.last_activity ? moment(i.last_activity).format('hh:mm A DD/MM/YYYY') : 'No activities',
                        };
                    }

                    // Check if this is activity data (has user_info and trail_detail)
                    if (i.user_info) {
                        // Handle activity data export
                        return {
                            id: i.id,
                            created_at: i.createdAt ? moment(i.createdAt).format('hh:mm A DD/MM/YYYY') : '',
                            province: i.trail_detail?.province || '',
                            trip_name: i.name || '',
                            activity_type: i.activity_type?.name || '',
                            trail: i.trail_detail ? 'Existed' : 'New',
                            trail_name: i.trail_detail?.name || '',
                            trip_address: i.trail_detail ? `${i.trail_detail?.address} ${i.trail_detail?.district} ${i.trail_detail?.province}` : '',
                            distance: i.distance || '',
                            link: i.link || '',
                        };
                    }

                    // Handle other data types (existing logic)
                    const getName = (list: any) => {
                        if (list && list?.length > 0) {
                            return list.map((i: any) => {
                                return i?.name ?? i?.key;
                            });
                        }
                        return ' ';
                    };
                    let activityName = getName(i.activity);
                    let marathon_attribute_typ = getName(i.marathon_attribute_type);
                    let suitability = getName(i.suitability);
                    let attractions = getName(i.attractions);
                    let conditions = getName(i.conditions);
                    let trail_traffics = getName(i.trail_traffics);
                    let parkings = getName(i.parkings);
                    let service_stay = getName(i.service_stay);
                    let take_away = getName(i.take_away);
                    let service_eat_local = getName(i.service_eat_local);
                    let door = getName(i.door);
                    let route_type = i?.route_type?.name;
                    return {
                        id: i.id,
                        name: i.name,
                        created_at: i.createdAt ? moment(i.createdAt).format('hh:mm A DD/MM/YYYY') : '',
                        address: i.address,
                        city: i?.city,
                        province: i?.province,
                        district: i?.district,
                        location: i?.location,
                        country: i?.country,
                        owner: i.owner_name ?? i.owner_info?.full_name,
                        phone_number: i.phone_number,
                        activity: activityName.toString(),
                        marathon_attribute_type: marathon_attribute_typ.toString(),
                        suitability: suitability.toString(),
                        attractions: attractions.toString(),
                        conditions: conditions.toString(),
                        route_type: route_type,
                        vehicle: trail_traffics.toString(),
                        parkings: parkings.toString(),
                        service_stay: service_stay.toString(),
                        take_away: take_away.toString(),
                        service_eat_local: service_eat_local.toString(),
                        door: door.toString(),
                        level: i.difficulty?.name,
                        status: i.status,
                        short_description: i.short_description,
                        description: i.description,
                        link: i.deep_link || i.link,
                        feature_image: i?.feature_image?.origin,
                        images: i?.images?.map((img: any) => img.origin).join(','),
                        elevation_gain: i?.elevation_gain,
                        price_from: i?.price_from,
                        price_to: i?.price_to,
                        price: i?.price,
                        opening_time: `${i?.opening_time?.from} - ${i?.opening_time?.to}`,
                        room: i?.room,
                        capacity: i?.capacity,
                        perimeter: i?.perimeter,
                    };
                });
                let columns = [{ header: 'ID', key: 'id' }];

                // Check if this is ranking data by looking at the first item
                const isRankingData = itemList.length > 0 && itemList[0].hasOwnProperty('rank') && itemList[0].hasOwnProperty('user_name');

                // Check if this is activity data by dataType prop or by checking raw data structure
                const isActivityData = dataType === 'activity' || (itemDraft.length > 0 && itemDraft[0].hasOwnProperty('user_info') && itemDraft[0].hasOwnProperty('trail_detail'));

                if (isUserData) {
                    // Define columns for user data export
                    columns = [
                        { header: 'ID', key: 'id' },
                        { header: 'Username', key: 'username' },
                        { header: 'First Name', key: 'first_name' },
                        { header: 'Last Name', key: 'last_name' },
                        { header: 'Email', key: 'email' },
                        { header: 'Created At', key: 'created_at' },
                    ];
                } else if (isRankingData) {
                    // Define columns for ranking data export
                    columns = [
                        { header: 'Rank', key: 'rank' },
                        { header: 'User Name', key: 'user_name' },
                        { header: 'Email', key: 'email' },
                        { header: 'Activities Count', key: 'activity_count' },
                        { header: 'Total Distance', key: 'total_distance' },
                        { header: 'Total Elevation Gain', key: 'total_elevation_gain' },
                        { header: 'Total Elevation Loss', key: 'total_elevation_loss' },
                        { header: 'Total Moving Time', key: 'total_moving_time' },
                        { header: 'Last Activity', key: 'last_activity' },
                    ];
                } else if (isActivityData) {
                    // Define columns for activity data export
                    columns = [
                        { header: 'ID', key: 'id' },
                        { header: 'Created At', key: 'created_at' },
                        { header: 'Province', key: 'province' },
                        { header: 'Activity name', key: 'trip_name' },
                        { header: 'Activity', key: 'activity_type' },
                        { header: 'Trail', key: 'trail' },
                        { header: 'Trail name', key: 'trail_name' },
                        { header: 'Address', key: 'trip_address' },
                        { header: 'Distance (km)', key: 'distance' },
                        { header: 'Link', key: 'link' },
                    ];
                } else if (category) {
                    columns = [...columns, { header: 'Feature Image', key: 'feature_image' }, { header: 'Images', key: 'images' }, { header: 'Name', key: 'name' }];
                } else {
                    columns = [...columns, { header: 'Name', key: 'name' }, { header: 'Status', key: 'status' }, { header: 'Owner', key: 'owner' }, { header: 'Description', key: 'description' }, { header: 'Link on App', key: 'link' }];
                }

                if (category === CATEGORY.TRAIL || category === CATEGORY.ECO_ZONES || category === CATEGORY.HERITAGE) {
                    columns = [
                        ...columns,
                        { header: 'Address', key: 'address' },
                        { header: 'Location', key: 'location' },
                        { header: 'District', key: 'district' },
                        { header: 'Province', key: 'province' },
                        { header: 'Country', key: 'country' },
                        { header: 'Length', key: 'perimeter' },
                        { header: 'Elevation gain', key: 'elevation_gain' },
                        { header: 'Route format', key: 'route_type' },
                        { header: 'Description', key: 'short_description' },
                        { header: 'Activity', key: 'activity' },
                        { header: 'Suitability', key: 'suitability' },
                        { header: 'Attractions', key: 'attractions' },
                        { header: 'Conditions', key: 'conditions' },
                        { header: 'Vehicle', key: 'vehicle' },
                        { header: 'Link on App', key: 'link' },
                    ];
                }
                if (category === CATEGORY.MARATHON) {
                    columns = [
                        ...columns,
                        { header: 'Address', key: 'address' },
                        { header: 'Location', key: 'location' },
                        { header: 'District', key: 'district' },
                        { header: 'Province', key: 'province' },
                        { header: 'Country', key: 'country' },
                        { header: 'Length', key: 'perimeter' },
                        { header: 'Elevation gain', key: 'elevation_gain' },
                        { header: 'Route format', key: 'route_type' },
                        { header: 'Description', key: 'short_description' },
                        { header: 'Level', key: 'level' },
                        {
                            header: 'Route format',
                            key: 'marathon_attribute_type',
                        },
                        { header: 'Attractions', key: 'attractions' },
                        { header: 'Conditions', key: 'conditions' },
                        { header: 'Link on App', key: 'link' },
                    ];
                }
                if (category === CATEGORY.STAY) {
                    columns = [
                        ...columns,
                        { header: 'Address', key: 'address' },
                        { header: 'Location', key: 'location' },
                        { header: 'District', key: 'district' },
                        { header: 'Province', key: 'province' },
                        { header: 'Country', key: 'country' },
                        { header: 'Description', key: 'short_description' },
                        { header: 'Price per night', key: 'price' },
                        { header: 'Room', key: 'room' },
                        { header: 'Capacity', key: 'capacity' },
                        { header: 'Vehicle', key: 'vehicle' },
                        { header: 'Parking', key: 'parkings' },
                        { header: 'Suitability', key: 'suitability' },
                        { header: 'Service', key: 'service_stay' },
                        { header: 'Attractions', key: 'attractions' },
                        { header: 'Link on App', key: 'link' },
                    ];
                }
                if (category === CATEGORY.EAT_LOCAL) {
                    columns = [
                        ...columns,
                        { header: 'Address', key: 'address' },
                        { header: 'Location', key: 'location' },
                        { header: 'District', key: 'district' },
                        { header: 'Province', key: 'province' },
                        { header: 'Country', key: 'country' },
                        { header: 'Description', key: 'short_description' },
                        { header: 'Price from', key: 'price_from' },
                        { header: 'Price to', key: 'price_to' },
                        { header: 'Price range', key: 'price' },
                        { header: 'Opening time', key: 'opening_time' },
                        { header: 'Vehicle', key: 'vehicle' },
                        { header: 'Parking', key: 'parkings' },
                        { header: 'Take away', key: 'take_away' },
                        { header: 'Service', key: 'service_eat_local' },
                        { header: 'Door', key: 'door' },
                        { header: 'Link on App', key: 'link' },
                    ];
                }

                const workbook = new Excel.Workbook();
                const workSheetName = 'Worksheet-1';

                // Normalize the search keyword to use as filename suffix
                let keywordSuffix = '';
                if (query.keyword) {
                    // Remove special characters and spaces, trim and convert to lowercase
                    keywordSuffix =
                        '-' +
                        normalizeVietnamese(
                            query.keyword
                                .trim()
                                .toLowerCase()
                                .replace(/\s+/g, '-') // Replace spaces with hyphens
                                .replace(/-+/g, '-')
                        ); // Replace multiple hyphens with single hyphen
                }

                let fileName = `Export ${category ?? 'challenges'}${keywordSuffix}`;

                // Update filename for user data
                if (isUserData) {
                    fileName = `Users-Export${keywordSuffix}`;
                }

                // Update filename for ranking data
                if (isRankingData) {
                    fileName = `User-Activity-Ranking${keywordSuffix}`;
                }

                // Update filename for activity data
                if (isActivityData) {
                    fileName = `Activities-Export${keywordSuffix}`;
                }

                const worksheet = workbook.addWorksheet(workSheetName);
                worksheet.columns = columns;
                worksheet.columns.forEach((column: any) => {
                    column.width = column.header.length + 5;
                    column.alignment = { horizontal: 'center' };
                });
                itemList.forEach((singleData: any) => {
                    worksheet.addRow(singleData);
                });

                worksheet.eachRow({ includeEmpty: false }, (row: any) => {
                    const currentCell = row._cells;

                    currentCell.forEach((singleCell: any) => {
                        const cellAddress = singleCell._address;

                        // apply border
                        worksheet.getCell(cellAddress).border = {
                            top: { style: 'thin' },
                            left: { style: 'thin' },
                            bottom: { style: 'thin' },
                            right: { style: 'thin' },
                        };
                    });
                });
                const buf = await workbook.xlsx.writeBuffer();
                saveAs(new Blob([buf]), `${fileName}.xlsx`);
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    };

    const onRemove = async (id: string) => {
        const { data, status } = await removeService(id);
        if (status === 200) {
            await sleep(2000);
            message.success('Remove success!');
            loadData().then();
        } else {
            message.error(data?.message);
        }
    };

    return (
        <TableContext.Provider
            value={{
                rows,
                setRows,
                loading,
                setLoading,
                total,
                setTotal,
                page,
                setPage,
                limit,
                setLimit,
                query,
                setQuery,
                onRemove,
                loadData,
                onExport,
                listService,
                initQuery,
                paging,
                dataType,
            }}
        >
            {children}
        </TableContext.Provider>
    );
};
export const useTable = () => React.useContext(TableContext);

export const TableHead = (props: any) => {
    const hook: any = useTable();
    const filters: Array<any> = props.filters || [];
    const disableSearch: boolean = props?.disableSearch;
    const [form] = Form.useForm();
    const [options, setOptions] = useState<{ value: string }[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);

    useEffect(() => {
        if (props?.isReset) {
            form.resetFields();
        }
    }, [props]);

    const handleSearch = (values: any) => {
        // Transform range values if they exist
        const transformedValues = Object.entries(values).reduce((acc: any, [key, value]) => {
            // Check if value is an array (range values)
            if (Array.isArray(value) && key.endsWith('_range')) {
                const baseKey = key.replace('_range', '');
                acc[`from_${baseKey}`] = value[0];
                acc[`to_${baseKey}`] = value[1];
            } else if (value !== undefined && value !== null && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {});

        hook.setQuery(transformedValues);
    };

    const onSelect = (value: string, option: any) => {
        // Set the form value
        form.setFieldsValue({ keyword: value });
        // Trigger the search
        handleSearch(form.getFieldsValue());
    };

    const onSearchChange = async (searchText: string) => {
        if (!searchText || !hook.listService) {
            setOptions([]);
            return;
        }

        setSearchLoading(true);
        try {
            // Get current form values
            const currentValues = form.getFieldsValue();

            // Create a temporary query with current form values and search text
            const tempQuery = {
                ...currentValues,
                keyword: searchText,
            };

            // Transform the values same way as handleSearch
            const transformedValues = Object.entries(tempQuery).reduce((acc: any, [key, value]) => {
                if (Array.isArray(value) && key.endsWith('_range')) {
                    const baseKey = key.replace('_range', '');
                    acc[`from_${baseKey}`] = value[0];
                    acc[`to_${baseKey}`] = value[1];
                } else if (value !== undefined && value !== null && value !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            // Use the listService to fetch suggestions
            const result = await hook.listService(
                hook.paging
                    ? {
                          page: 1,
                          limit: 5, // Limit suggestions to 10 items
                          ...transformedValues,
                          ...(hook.initQuery || {}),
                      }
                    : {
                          ...transformedValues,
                          ...(hook.initQuery || {}),
                      }
            );

            if (result.status === 200 && result?.data?.data?.items?.length >= 0) {
                const items = hook.paging ? result.data.data.items : result.data.data;
                // Instead of reducing to unique strings, create structured options
                const suggestionOptions = items.map((item: { name: string; address?: string; admin?: { email: string } }) => ({
                    value: item.name, // Value to be used when selected
                    // Custom render of the option
                    label: (
                        <div style={{ padding: '8px 0' }}>
                            <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                            {item.address && (
                                <div
                                    style={{
                                        fontSize: '12px',
                                        color: '#666',
                                    }}
                                >
                                    {item.address}
                                </div>
                            )}
                            {item.admin?.email && (
                                <div
                                    style={{
                                        fontSize: '12px',
                                        color: '#666',
                                    }}
                                >
                                    {item.admin?.email}
                                </div>
                            )}
                        </div>
                    ),
                    item: item, // Store the full item for reference if needed
                }));
                setOptions(suggestionOptions);
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setOptions([]);
        } finally {
            setSearchLoading(false);
        }
    };

    // Debounce the search to prevent too many API calls
    const debouncedSearch = React.useCallback(
        debounce((text: string) => onSearchChange(text), 300),
        []
    );

    return (
        <TableHeadContainer>
            <TableHeadTitle>{props.title.replace('${COUNT}', hook.total || hook?.rows?.length || 0)}</TableHeadTitle>
            <TableHeadFilter>
                <Form
                    form={form}
                    onFinish={(values) => {
                        hook.setPage(1);

                        // Transform date values to YYYY-MM-DD format for API
                        const transformedValues = { ...values };

                        // Handle date_range (RangePicker) - convert to from_date and to_date
                        if (values.date_range && Array.isArray(values.date_range) && values.date_range.length === 2) {
                            console.log(values.date_range);

                            transformedValues.from_date = values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined;
                            transformedValues.to_date = values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined;
                            delete transformedValues.date_range; // Remove the range field itself
                        }

                        // Format individual date fields
                        if (values.startDate && moment.isMoment(values.startDate)) {
                            transformedValues.startDate = values.startDate.format('YYYY-MM-DD');
                        }
                        if (values.endDate && moment.isMoment(values.endDate)) {
                            transformedValues.endDate = values.endDate.format('YYYY-MM-DD');
                        }
                        // Handle from_date and to_date for activity filters (if provided separately)
                        if (values.from_date && moment.isMoment(values.from_date)) {
                            transformedValues.from_date = values.from_date.format('YYYY-MM-DD');
                        }
                        if (values.to_date && moment.isMoment(values.to_date)) {
                            transformedValues.to_date = values.to_date.format('YYYY-MM-DD');
                        }

                        hook.setQuery({ ...hook.query, ...transformedValues });
                    }}
                >
                    {filters?.length ? (
                        <div className={`filters ${!disableSearch ? 'search' : ''}`}>
                            {filters.map((value, index) => {
                                return (
                                    <Form.Item key={index.toString()} hidden={value?.hidden} initialValue={value?.initialValue} name={value.name}>
                                        {value?.control || null}
                                    </Form.Item>
                                );
                            })}
                            <Button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    form.submit();
                                }}
                            >
                                Apply
                            </Button>
                        </div>
                    ) : null}
                    {!disableSearch ? (
                        <div className={'search'}>
                            <div className={'search-form'}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M6.84199 13.684C8.36004 13.6837 9.83434 13.1755 11.0301 12.2403L14.7898 16L15.9991 14.7907L12.2395 11.031C13.1751 9.83508 13.6836 8.36043 13.684 6.84199C13.684 3.06949 10.6145 0 6.84199 0C3.06949 0 0 3.06949 0 6.84199C0 10.6145 3.06949 13.684 6.84199 13.684ZM6.84199 1.7105C9.67201 1.7105 11.9735 4.01197 11.9735 6.84199C11.9735 9.67201 9.67201 11.9735 6.84199 11.9735C4.01197 11.9735 1.7105 9.67201 1.7105 6.84199C1.7105 4.01197 4.01197 1.7105 6.84199 1.7105Z"
                                        fill="black"
                                        fillOpacity="0.5"
                                    />
                                </svg>
                                <Form.Item name={'keyword'}>
                                    <AutoComplete
                                        placeholder={searchLoading ? 'Loading...' : 'Keyword'}
                                        options={options}
                                        onSearch={debouncedSearch}
                                        onSelect={onSelect}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                form.submit();
                                            }
                                        }}
                                        style={{ width: '100%' }}
                                        dropdownStyle={{
                                            maxHeight: '400px',
                                            overflow: 'auto',
                                            padding: '8px 0',
                                        }}
                                        dropdownMatchSelectWidth={500}
                                        notFoundContent={searchLoading ? <span>Loading suggestions...</span> : undefined}
                                    />
                                </Form.Item>
                            </div>
                            <Button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    form.submit();
                                }}
                            >
                                Search
                            </Button>
                        </div>
                    ) : null}
                    {props?.showExport ? (
                        <div className={'search-export'}>
                            <Button
                                onClick={() => {
                                    hook.onExport();
                                }}
                            >
                                Export
                            </Button>
                        </div>
                    ) : (
                        <></>
                    )}
                </Form>
            </TableHeadFilter>
        </TableHeadContainer>
    );
};

export const TableBody = (props: any) => {
    const hook: any = useTable();
    const ignoreItems: Array<any> = props.ignoreItems || [];

    return (
        <TableView
            {...props}
            dataSource={hook.rows?.filter((item: any) => !ignoreItems.find((x) => x.id === item.id))}
            loading={hook.loading}
            pagination={false}
            onChange={(pagination, filters, sorter: any) => {
                const sortKey = sorter?.field;
                const sortBy = sorter?.order === 'ascend' ? 'asc' : 'desc';
                hook.setQuery(
                    produce((draft: any) => {
                        draft.sortKey = sorter?.order ? sortKey : undefined;
                        draft.sortBy = sorter?.order ? sortBy : undefined;
                    })
                );
            }}
        />
    );
};

export const TableFooter = (props: any) => {
    const hook: any = useTable();

    return (
        <TableFooterContainer>
            <TableFooterSize>
                <span>Show</span>
                <Select
                    value={hook.limit}
                    onChange={(value) => {
                        hook.setPage(1);
                        hook.setLimit(value);
                    }}
                >
                    {[10, 30, 50, 100].map((value, index) => (
                        <Select.Option value={value} key={index.toString()}>
                            {value}
                        </Select.Option>
                    ))}
                </Select>
                <span>items/page</span>
            </TableFooterSize>

            <TableFooterPagination
                showSizeChanger={false}
                pageSize={hook?.limit || 0}
                current={hook.page}
                onChange={(page, pageSize) => {
                    hook.setPage(page);
                }}
                total={hook.total}
            />
        </TableFooterContainer>
    );
};

export const FilterStatusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
];

export const TableHeadFilterSelect = (props: any) => {
    return <Select {...props} />;
};

export const TableHeadFilterServiceSelect = (props: any) => {
    return <SelectFormServiceView {...props} />;
};

export const TableActions = (props: any) => {
    const hook: any = useTable();

    return (
        <ActionsView
            style={props.style}
            onNext={props?.onNext}
            onReject={
                props?.onReject
                    ? async () => {
                          await props?.onReject();
                          await hook.loadData();
                      }
                    : undefined
            }
            onAccept={
                props?.onAccept
                    ? async () => {
                          await props?.onAccept();
                          await hook.loadData();
                      }
                    : undefined
            }
            onShare={props?.share}
            onEdit={props?.edit?.success}
            removeTitle={props?.remove?.title}
            onRemove={props?.onRemove ? props?.onRemove : props?.remove ? () => hook.onRemove(props.remove.id) : null}
        />
    );
};

export const TableBodyWithValue = (props: any) => {
    return <TableView {...props} pagination={false} />;
};

export const TabEditView2 = (props: any) => {
    const hook: any = useTable();
    return (
        <TabEditView
            {...props}
            onChange={(value: any) => {
                props.onChange(value);
                hook.setQuery({});
                hook.setLoading();
            }}
        />
    );
};
